import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {
  AangifteBerekening,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  Observable,
  shareReplay,
  switchMap,
  take
} from "rxjs";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-overzicht-inkomen-box1',
    templateUrl: './overzicht-inkomen-box1.component.html',
    styleUrls: ['./overzicht-inkomen-box1.component.scss'],
    imports: [SharedUiFormControlsModule, SharedUiDetailsModule, CommonModule],
})
export class OverzichtInkomenBox1Component implements OnInit {
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private bokkzClient = inject(IbRekenService);
    public dataVisible$ = new BehaviorSubject<boolean>(false);
    public isDefAangifte = false;
    public isPartner = false;
    public isPartnerView = false;

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public formGroup = new FormGroup({
        winstUitOnderneming: new FormControl<number | null>(0),
        belastbaarloon: new FormControl<number | null>(0),
        resultaatUitOverigeWerkzaamheden: new FormControl<number | null>(0),
        periodiekeUitkeringen: new FormControl<number | null>(0),
        inkomstenUitEigenWoning: new FormControl<number | null>(0),
        saldoEigenWoning: new FormControl<number | null>(0),
        negatieveUitgavenVoorInkomensvoorzieningen: new FormControl<number | null>(0),
        negatievePersoonsgebondenAftrek: new FormControl<number | null>(0),
        overigeInkomsten: new FormControl<number | null>(0),
        aftrekWegensGeenOfGeringeEigenWoningSchuld: new FormControl<number | null>(0),
        uitgavenVoorInkomensvoorzieningen: new FormControl<number | null>(0),
        verrekendVerlies: new FormControl<number | null>(0),
        teConserverenInkomen: new FormControl<number | null>(0),
        inkomen: new FormControl<number | null>(0),
        persoonsgebondenAftrek: new FormControl<number | null>(0),
        belastbaarInkomen: new FormControl<number | null>(0),
    });

    ngOnInit(): void {
        const berekening$ = this.incomeTaxEntity$.pipe(
            take(1),
            filter((data): data is IncomeTaxEntity => !!data.aangifte),
            switchMap((data) => {
                this.isDefAangifte = data.taxType === 'DA';
                return this.bokkzClient.getIncomeTaxCalculation(data.aangifte!);
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
            takeUntilDestroyed(this.destroyRef),
        );

        combineLatest([berekening$, this.dataService.partnerAangifte$.pipe(distinctUntilChanged())]).subscribe(
            ([berekening, isPartner]) => {
                this.isPartnerView = isPartner; // ← toevoegen
                this.dataVisible$.next(false);
                this.resetAndPatchForms(isPartner ? (berekening.partnerBerekening ?? berekening) : berekening);
            },
        );
    }

    private resetAndPatchForms(details: AangifteBerekening): void {
        this.formGroup.reset({}, { emitEvent: false });
        this.formGroup.patchValue(details.box1?.opbouwInkomen ?? {}, { emitEvent: false });
        this.formGroup.patchValue({ inkomen: details.box1?.inkomen }, { emitEvent: false });
        this.formGroup.patchValue({ persoonsgebondenAftrek: details.box1?.persoonsgebondenAftrek }, { emitEvent: false });
        this.formGroup.patchValue({ belastbaarInkomen: details.box1?.belastbaarInkomen }, { emitEvent: false });
        this.formGroup.disable({ emitEvent: false });
        this.dataVisible$.next(true);
    }
}
